import { useCallback, useEffect, useRef, useState } from 'react';
import { elegirCodigo, FORMATOS_1D } from '../components/BarcodeScanner/serieMatcher';

const FRAME_INTERVALO_MS = 1000 / 6; // ~6 fps: de sobra para 1D, cuida batería/CPU en tablet 24/7

export const ESTADOS = {
    INACTIVO: 'inactivo',
    INICIANDO: 'iniciando',
    LEYENDO: 'leyendo',
    ERROR: 'error',
};

export const ERRORES = {
    CONTEXTO_INSEGURO: 'CONTEXTO_INSEGURO',
    PERMISO_DENEGADO: 'PERMISO_DENEGADO',
    SIN_CAMARA: 'SIN_CAMARA',
    CAMARA_OCUPADA: 'CAMARA_OCUPADA',
    DESCONOCIDO: 'DESCONOCIDO',
};

// CONTEXTO_INSEGURO no tiene mensaje propio: BarcodeScanner lo intercepta y
// muestra el modo foto en su lugar (getUserMedia no existe en HTTP, pero
// <input type="file" capture> sí funciona sin contexto seguro).
const mensajesError = {
    [ERRORES.PERMISO_DENEGADO]: 'Permiso de cámara denegado. Habilítalo en los ajustes del navegador.',
    [ERRORES.SIN_CAMARA]: 'No se encontró ninguna cámara en este dispositivo.',
    [ERRORES.CAMARA_OCUPADA]: 'La cámara está siendo usada por otra aplicación.',
    [ERRORES.DESCONOCIDO]: 'No se pudo iniciar la cámara. Intenta de nuevo.',
};

function mapearErrorGetUserMedia(e) {
    if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') return ERRORES.PERMISO_DENEGADO;
    if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') return ERRORES.SIN_CAMARA;
    if (e?.name === 'NotReadableError') return ERRORES.CAMARA_OCUPADA;
    return ERRORES.DESCONOCIDO;
}

// Nativo primero (BarcodeDetector, cero peso de bundle); si el navegador no
// lo trae o el dispositivo no tiene el módulo de códigos descargado
// (getSupportedFormats devuelve [] en algunos Android), cae a ZXing cargado
// bajo demanda para no engordar el bundle de quien nunca escanea.
async function crearDetector() {
    if ('BarcodeDetector' in window) {
        try {
            const soportados = await window.BarcodeDetector.getSupportedFormats();
            const utilizables = FORMATOS_1D.filter((f) => soportados.includes(f));
            if (utilizables.length > 0) {
                const detector = new window.BarcodeDetector({ formats: utilizables });
                return {
                    tipo: 'nativo',
                    detectar: async (video) => {
                        const resultados = await detector.detect(video);
                        return resultados.map((r) => r.rawValue);
                    },
                };
            }
        } catch (e) {
            // getSupportedFormats puede rechazar en navegadores que declaran
            // el símbolo pero no lo implementan del todo — se sigue a ZXing.
        }
    }

    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const reader = new BrowserMultiFormatReader();
    return {
        tipo: 'zxing',
        detectar: async (video) => {
            try {
                const resultado = await reader.decodeOnce(video);
                return [resultado.getText()];
            } catch (e) {
                return [];
            }
        },
    };
}

/**
 * Ciclo de vida completo de la cámara: permisos, decodificación y linterna.
 * `activo` controla apertura/cierre; el hook no expone nada para arrancar
 * "a mano" porque toda la limpieza depende de ese único interruptor.
 */
export default function useBarcodeScanner({ activo, patron, onDetectado }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const detectorRef = useRef(null);
    const rafRef = useRef(null);
    const ultimoFrameRef = useRef(0);
    const detectandoRef = useRef(false);
    const cerradoRef = useRef(false);

    const [estado, setEstado] = useState(ESTADOS.INACTIVO);
    const [error, setError] = useState(null);
    const [linternaDisponible, setLinternaDisponible] = useState(false);
    const [linternaEncendida, setLinternaEncendida] = useState(false);
    const [cicloId, setCicloId] = useState(0);

    const detenerTodo = useCallback(() => {
        cerradoRef.current = true;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
        detectorRef.current = null;
        setLinternaDisponible(false);
        setLinternaEncendida(false);
    }, []);

    const alternarLinterna = useCallback(async () => {
        const track = streamRef.current?.getVideoTracks?.()[0];
        if (!track) return;
        try {
            const siguiente = !linternaEncendida;
            await track.applyConstraints({ advanced: [{ torch: siguiente }] });
            setLinternaEncendida(siguiente);
        } catch (e) {
            // Dispositivo dijo soportar torch pero falló al aplicarlo — no es
            // fatal para el escaneo, simplemente no queda linterna disponible.
            setLinternaDisponible(false);
        }
    }, [linternaEncendida]);

    useEffect(() => {
        if (!activo) {
            detenerTodo();
            setEstado(ESTADOS.INACTIVO);
            setError(null);
            return undefined;
        }

        cerradoRef.current = false;
        setEstado(ESTADOS.INICIANDO);
        setError(null);

        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
            setEstado(ESTADOS.ERROR);
            setError(ERRORES.CONTEXTO_INSEGURO);
            return undefined;
        }

        let cancelado = false;

        (async () => {
            try {
                const [stream, detector] = await Promise.all([
                    navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: { ideal: 'environment' },
                            width: { ideal: 1920 },
                            height: { ideal: 1080 },
                        },
                        audio: false,
                    }),
                    crearDetector(),
                ]);

                if (cancelado || cerradoRef.current) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                streamRef.current = stream;
                detectorRef.current = detector;

                const track = stream.getVideoTracks()[0];
                const capacidades = track?.getCapabilities?.() || {};
                setLinternaDisponible(!!capacidades.torch);

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play().catch(() => {});
                }

                setEstado(ESTADOS.LEYENDO);

                const loop = async (timestamp) => {
                    if (cerradoRef.current) return;
                    if (
                        !detectandoRef.current &&
                        timestamp - ultimoFrameRef.current >= FRAME_INTERVALO_MS &&
                        videoRef.current?.readyState >= 2
                    ) {
                        ultimoFrameRef.current = timestamp;
                        detectandoRef.current = true;
                        try {
                            const codigos = await detectorRef.current.detectar(videoRef.current);
                            const elegido = elegirCodigo(codigos, patron);
                            if (elegido && !cerradoRef.current) {
                                onDetectado(elegido);
                                detectandoRef.current = false;
                                return; // el consumidor cierra el modal vía `activo`
                            }
                        } catch (e) {
                            // Frame fallido individual: no es un error del escaneo en
                            // conjunto, se sigue intentando en el próximo frame.
                        }
                        detectandoRef.current = false;
                    }
                    rafRef.current = requestAnimationFrame(loop);
                };
                rafRef.current = requestAnimationFrame(loop);
            } catch (e) {
                if (cancelado || cerradoRef.current) return;
                setEstado(ESTADOS.ERROR);
                setError(mapearErrorGetUserMedia(e));
            }
        })();

        return () => {
            cancelado = true;
            detenerTodo();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activo, cicloId]);

    // Cámara viva de más en una tablet 24/7 es batería y calor: se libera al
    // ocultar la pestaña y se reintenta el ciclo completo al volver.
    useEffect(() => {
        if (!activo) return undefined;
        const onVisibility = () => {
            if (document.hidden) {
                detenerTodo();
                setEstado(ESTADOS.INACTIVO);
            } else {
                // Fuerza al efecto principal a reabrir la cámara: `activo`
                // no cambió, así que sin este empujón se quedaría inactivo.
                setCicloId((n) => n + 1);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [activo, detenerTodo]);

    return {
        videoRef,
        estado,
        error: error ? mensajesError[error] : null,
        errorCodigo: error,
        linternaDisponible,
        linternaEncendida,
        alternarLinterna,
    };
}
